import { useState, type FormEvent } from 'react'
import { Button, Field } from './ui'

/**
 * The second step of signing in: a code from the authenticator app, or a
 * recovery code. Shared by the password login and the GitHub callback, which
 * both land here when the account has a second factor.
 */
export default function MfaForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (code: string) => Promise<void>
  onCancel: () => void
}) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await onSubmit(code.trim())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code did not work.')
      setCode('')
      setBusy(false)
    }
  }

  return (
    <form className="stack" onSubmit={submit}>
      <div className="stack" style={{ gap: 4 }}>
        <h1>Two-factor code</h1>
        <p className="muted">
          Open your authenticator app and enter the six-digit code for
          TalariCloud — or one of your recovery codes.
        </p>
      </div>

      {error && <div className="alert error">{error}</div>}

      <Field label="Code" htmlFor="mfa-code">
        <input
          id="mfa-code"
          className="mono"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
          minLength={6}
          maxLength={16}
          placeholder="123 456"
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
      </Field>

      <Button variant="primary" type="submit" loading={busy} block disabled={code.trim().length < 6}>
        Continue
      </Button>
      <Button variant="ghost" type="button" block onClick={onCancel}>
        Back to sign in
      </Button>
    </form>
  )
}
